import React, { useEffect, useMemo, useRef, useState } from "react";
import { Table, Tag, Tooltip, Typography } from "antd";
import FilterDateRangePicker from "./FilterDateRangePicker";
import FilterSearchInput from "./FilterSearchInput";
import type { Dayjs } from "dayjs";
import {
  CalendarDaysIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { useAlerts } from "../../hooks/useAppAlerts";
import { useListDevicesQuery } from "../../actions/devicesInQuery";
import { useListTrapSitesQuery } from "../../actions/trapsiteInQuery";
import { ALERT_STATUS, ALERT_TYPE } from "../../types";
import "./index.css";
import { useMinDurationLoading } from "../../hooks/useMinDurationLoading";
import { useDebounce } from "../../hooks/useDebounce";
import type { AlertSorter } from "../../types/api/request/alert";
import AlertModal from "./AlertModal";
import { formatDate } from "../../utils/helpers";
import { ALERT_TITLES } from "../../utils/const";
import { getActiveOrg } from "../../utils/activeOrg";
import type { AlertFilters, AlertTableRecord } from "./alertTableRows";
import {
  INITIAL_FILTERS,
  TYPE_LABEL,
  deviceFilterOptions,
  deviceNameMap as buildDeviceNameMap,
  deviceOrgMap as buildDeviceOrgMap,
  deviceTrapSiteMap as buildDeviceTrapSiteMap,
  pageAfterChange,
  toAlertFilters,
  toAlertSorter,
  toAlertTableRows,
} from "./alertTableRows";

const { Text } = Typography;

const PAGE_SIZE = 20;
const TABLE_HEADER_HEIGHT = 54;
const PAGINATION_HEIGHT = 40;
const ORG_COLUMN_WIDTH = 200;
const serverSorter = (): number => 0;

/**
 * `org` renders another organization's alerts, for the superuser panel.
 *
 * `orgNames` adds an Organization column, for the panel's All view where the
 * alerts span organizations, and locks the read toggle on any alert outside
 * the organization the operator is acting as.
 */
const AlertTable: React.FC<{
  org?: string;
  orgNames?: Map<string, string>;
}> = ({ org, orgNames }) => {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<AlertFilters>(INITIAL_FILTERS);
  const [idSearch, setIdSearch] = useState("");
  const [selectedRecord, setSelectedRecord] = useState<AlertTableRecord | null>(
    null,
  );
  const [sorter, setSorter] = useState<AlertSorter>({ createdAt: -1 });
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState(400);

  // Must carry `org` too: this map turns an alert's device uuid into a display
  // name, and the column filter is built from the same list. Without it the
  // panel resolved another organization's alerts against the CURRENT
  // organization's devices, so every device rendered as an em dash.
  const { data: devicesData, isLoading: isDevicesLoading } =
    useListDevicesQuery(org ? { org } : undefined);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      setScrollY(
        Math.max(
          200,
          entry.contentRect.height - TABLE_HEADER_HEIGHT - PAGINATION_HEIGHT,
        ),
      );
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const updateFilters = (update: Partial<AlertFilters>) => {
    setFilters((prev) => ({ ...prev, ...update }));
    setPage(1);
  };

  const debouncedIdSearch = useDebounce(idSearch, 700);

  const zeroBasedPage = page - 1;

  const {
    data: alertData,
    isLoading,
    isFetching,
  } = useAlerts({
    ...filters,
    msg_types: filters.messages,
    idSearch: debouncedIdSearch,
    sorter,
    page: zeroBasedPage,
    pageSize: PAGE_SIZE,
    org,
  });

  const isDataLoading = useMinDurationLoading(isLoading || isFetching, {
    ...filters,
    idSearch: debouncedIdSearch,
    ...sorter,
    page: zeroBasedPage,
    pageSize: PAGE_SIZE,
  });

  const { data: trapsitesData } = useListTrapSitesQuery(
    org ? { org } : undefined,
  );

  const deviceNameMap = useMemo(
    () => buildDeviceNameMap(devicesData?.items),
    [devicesData],
  );

  const deviceOrgMap = useMemo(
    () =>
      orgNames ? buildDeviceOrgMap(devicesData?.items, orgNames) : undefined,
    [devicesData, orgNames],
  );

  const deviceTrapSiteMap = useMemo(
    () => buildDeviceTrapSiteMap(devicesData?.items, trapsitesData?.items),
    [devicesData, trapsitesData],
  );

  const tableData = useMemo(
    () =>
      toAlertTableRows(
        alertData?.items,
        deviceNameMap,
        deviceTrapSiteMap,
        deviceOrgMap,
      ),
    [alertData, deviceNameMap, deviceTrapSiteMap, deviceOrgMap],
  );

  // Changing read status acts as the operator's own organization, and the
  // server only finds alerts on that organization's devices, so in the All view
  // any other organization's alert would just fail to update. Locked too when
  // the operator's own organization is unknown, since none can be confirmed.
  const statusLockedReason =
    selectedRecord && deviceOrgMap && selectedRecord.org !== getActiveOrg()
      ? `This alert belongs to ${selectedRecord.orgName || "another organization"}. Switch to that organization to change its read status.`
      : undefined;

  // The capture page reads as the operator's own organization too, so another
  // organization's capture, whether in the All view or the panel's view of a
  // single organization, would only fail to load.
  const selectedOrg = selectedRecord?.org ?? org;
  const meshCaptureLockedReason =
    selectedRecord?.meshCaptureId &&
    selectedOrg !== undefined &&
    selectedOrg !== getActiveOrg()
      ? `This alert belongs to ${selectedRecord.orgName || "another organization"}. Switch to that organization to view its capture.`
      : undefined;

  const sortIcon = useMemo(
    () =>
      ({ sortOrder }: { sortOrder: "ascend" | "descend" | null }) => {
        if (sortOrder === "ascend")
          return <ChevronUpIcon className="w-5 h-5 text-[#003A55] ml-1" />;
        if (sortOrder === "descend")
          return <ChevronDownIcon className="w-5 h-5 text-[#003A55] ml-1" />;
        return (
          <ChevronDownIcon className="w-5 h-5 text-[#003A55] opacity-30 ml-1" />
        );
      },
    [],
  );
  const columns = useMemo(
    () => [
      {
        title: "ID",
        dataIndex: "alertId",
        key: "alertId",
        width: 160,
        filterDropdown: ({ confirm }: { confirm: () => void }) => (
          <div className="p-2">
            <FilterSearchInput
              placeholder="Search ID"
              value={idSearch}
              onChange={(e) => {
                setIdSearch(e.target.value);
                setPage(1);
              }}
              onSearch={confirm}
              onPressEnter={confirm}
              allowClear
              onClear={() => {
                setIdSearch("");
                setPage(1);
                confirm();
              }}
            />
          </div>
        ),
        filteredValue: null,
        filterIcon: () => (
          <MagnifyingGlassIcon
            className={`alert-table-filter-icon ${idSearch ? "active" : ""}`}
          />
        ),
        onFilter: (): boolean => true,
        render: (id: string) => (
          <Text className="font-mono text-sm text-gray-500">{id}</Text>
        ),
      },
      {
        title: "Type",
        dataIndex: "type",
        key: "type",
        width: 160,
        sorter: serverSorter,
        sortIcon,
        filters: [
          { text: "Alert", value: "maintenance" },
          { text: "Notification", value: "notification" },
          { text: "Detection", value: "detection" },
        ],
        filteredValue: filters.types,
        onFilter: (): boolean => true,
        render: (type: ALERT_TYPE) => (
          <Tag className={`alert-table-type-tag ${type}`}>
            {TYPE_LABEL[type]}
          </Tag>
        ),
      },

      {
        title: "Trap",
        dataIndex: "deviceName",
        key: "deviceName",
        width: 230,
        sorter: serverSorter,
        sortIcon,
        filters: deviceFilterOptions(devicesData?.items, deviceOrgMap),
        filteredValue: filters.deviceIds,
        filterSearch: true,
        onFilter: (): boolean => true,
        render: (name: string) => <Text>{name || "—"}</Text>,
      },
      // Not sortable or filterable: alerts carry no organization of their own,
      // so the server cannot order or match by it. The Trap filter names each
      // trap's organization instead.
      ...(deviceOrgMap
        ? [
            {
              title: "Organization",
              dataIndex: "orgName",
              key: "orgName",
              width: ORG_COLUMN_WIDTH,
              render: (name: string) => <Text>{name || "—"}</Text>,
            },
          ]
        : []),
      {
        title: "Short Description",
        dataIndex: "message",
        key: "description",
        onHeaderCell: () => ({ style: { minWidth: 150 } }),
        onCell: () => ({
          style: {
            minWidth: 150,
            maxWidth: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          },
        }),
        filters: ALERT_TITLES.map((t) => ({ text: t.label, value: t.value })),
        filteredValue: filters.messages,
        filterSearch: true,
        filterDropdownProps: {
          rootClassName: "alert-description-filter-dropdown",
        },
        onFilter: (): boolean => true,
        render: (message: string, record: AlertTableRecord) => (
          <Tooltip title={record.description} placement="topLeft">
            <Text className="block truncate">{message}</Text>
          </Tooltip>
        ),
      },
      {
        title: "Date / Time",
        dataIndex: "createdAt",
        key: "createdAt",
        width: 250,
        sorter: serverSorter,
        defaultSortOrder: "descend" as const,
        sortIcon,
        filterDropdown: ({ confirm }: { confirm: () => void }) => (
          <div className="p-2">
            <FilterDateRangePicker
              value={filters.dateRange}
              onChange={(range) => {
                updateFilters({ dateRange: range as [Dayjs, Dayjs] | null });
                confirm();
              }}
              allowClear
            />
          </div>
        ),
        filteredValue: null,
        filterIcon: () => (
          <CalendarDaysIcon
            className={`alert-table-filter-icon ${filters.dateRange ? "active" : ""}`}
          />
        ),
        onFilter: (): boolean => true,
        render: (date: Date) => (
          <Text className="text-gray-600">{formatDate(date)}</Text>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 160,
        sorter: serverSorter,
        sortIcon,
        filters: [
          { text: "unread", value: "unread" },
          { text: "read", value: "read" },
        ],
        filteredValue: filters.statuses,
        onFilter: (): boolean => true,
        render: (status: ALERT_STATUS) => {
          if (status === "unread")
            return (
              <div className="flex justify-center">
                <Tag className="alert-table-status-tag unread">unread</Tag>
              </div>
            );
          if (status === "read")
            return (
              <div className="flex justify-center">
                <Tag className="alert-table-status-tag read">read</Tag>
              </div>
            );
          return null;
        },
      },
    ],
    [sortIcon, filters, idSearch, devicesData?.items, deviceOrgMap],
  );

  return (
    <div
      ref={containerRef}
      className="bg-white p-6 rounded-lg border border-[#0C4A6E] flex-1 min-h-0 flex flex-col"
    >
      <Table<AlertTableRecord>
        columns={columns}
        dataSource={tableData}
        loading={isDevicesLoading || isDataLoading}
        locale={{
          emptyText: (
            <div
              className="flex items-center justify-center text-gray-400"
              style={{ height: scrollY }}
            >
              {!isDevicesLoading && !isDataLoading && <p>No alerts found</p>}
            </div>
          ),
        }}
        className="alert-table"
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: alertData?.count,
          showSizeChanger: false,
          showTotal: (total) => `${total} alert${total === 1 ? "" : "s"}`,
        }}
        onRow={(record) => ({
          onClick: () => setSelectedRecord(record),
          style: { cursor: "pointer" },
        })}
        scroll={{
          y: scrollY,
          x: 1160 + (deviceOrgMap ? ORG_COLUMN_WIDTH : 0),
        }}
        onChange={(pagination, columnFilters, sorterResult, extra) => {
          setFilters((prev) => ({
            ...prev,
            ...toAlertFilters(columnFilters),
          }));

          setSorter(toAlertSorter(sorterResult));

          setPage(pageAfterChange(extra.action, pagination.current));
        }}
      />
      <AlertModal
        statusLockedReason={statusLockedReason}
        meshCaptureId={selectedRecord?.meshCaptureId}
        meshCaptureLockedReason={meshCaptureLockedReason}
        open={selectedRecord !== null}
        onClose={() => setSelectedRecord(null)}
        alertId={selectedRecord?.key ?? ""}
        type={selectedRecord?.type ?? "maintenance"}
        deviceName={selectedRecord?.deviceName ?? ""}
        trapsiteName={selectedRecord?.trapsiteName ?? ""}
        trapsiteAddress={selectedRecord?.trapsiteAddress ?? ""}
        createdAt={selectedRecord?.createdAt ?? new Date()}
        status={selectedRecord?.status ?? "unread"}
        description={selectedRecord?.description ?? ""}
        message={selectedRecord?.message ?? ""}
      />
    </div>
  );
};

export default AlertTable;
